import { useMemo } from "react";

import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useT } from "@repo/transifex";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { shouldHideBarcodedProducts } from "~/utils/tills/should-hide-barcoded-products";
import { type Category } from "~/api/products/types";

import { useActiveCategoryId } from "./use-basket";

export const usePosCategories = (): {
  categories: Category[];
  status: "success" | "pending" | "error";
} => {
  const { data: till } = useCurrentTill();
  const { data: shouldHideBarcoded } = useCurrentTill(
    shouldHideBarcodedProducts
  );
  const { data, status } = useUserProductsPos();
  const t = useT();

  return useMemo(() => {
    if (status !== "success") {
      return {
        categories: [],
        status
      };
    }

    // Filter products based on barcode visibility setting
    const visibleProducts = shouldHideBarcoded
      ? data.products.filter((product) => product.barcodeIds.length === 0)
      : data.products;

    // Filter categories to only include those with visible products
    let categories = data.categories.filter((category) =>
      visibleProducts.some((product) => product.categoryId === category.id)
    );

    const hasPopularProducts = visibleProducts.some((product) =>
      product.tags.includes("popular")
    );

    if (hasPopularProducts && till?.type !== "self-service") {
      categories = [
        {
          id: "popular",
          name: "⭐"
        },
        ...categories
      ];
    }

    if (
      (hasAttendedSellConfig(till) || till?.type === "self-service") &&
      till.config.showAllCategory &&
      visibleProducts.length > 0
    ) {
      categories = [
        {
          id: "all",
          name: t("All")
        },
        ...categories
      ];
    }

    return {
      categories,
      status
    };
  }, [till, data, status, t, shouldHideBarcoded]);
};

export const usePosActiveCategory = (): Category | null => {
  const { categories } = usePosCategories();
  const activeCategoryId = useActiveCategoryId();

  return useMemo(() => {
    if (categories.length === 0) {
      return null;
    }

    if (!activeCategoryId) {
      return categories[0];
    }

    return (
      categories.find((category) => category.id === activeCategoryId) ?? null
    );
  }, [categories, activeCategoryId]);
};
